import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import styles from '../../InpatientPortal.module.css';
import { useSelector } from 'react-redux';
import { formatValueByModalityId, getVitalUnit } from '../../../../utils/unitUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);


const InpatientVitalChart = ({ vitalConfig, measurements, onPointClick }) => {
    const {dateFormat, unitType} = useSelector((state) => state.userInfo);


    
    // Filter measurements for the selected vital's modality
    const chartValues = useMemo(() => {
        if (!vitalConfig || !measurements) return [];
        return measurements.filter(measurement => measurement.modalityId === vitalConfig.modalityId);
    }, [vitalConfig, measurements]);
    
    // Calculate values before hooks - safe even if vitalConfig is null
    const modalityId = vitalConfig?.modalityId || null;
    
    // Convert threshold values from base units (Celsius/Kilograms)
    const convertValue = useMemo(() => {
        if (!vitalConfig) return () => null;
        
        const vitalName = vitalConfig.name || '';
        
        return (value, isThreshold = false) => {
            if (value === 0 || value === undefined || value === null) return value;

            if (vitalName === 'temperature') {
                if (unitType === 'Imperial') {
                    const val = isThreshold ? (value * 9 / 5) + 32 : value;
                    return parseFloat(val.toFixed(1));
                }
                return parseFloat(value.toFixed(1));
            } else if (vitalName === 'weight') {
                if (unitType === 'Imperial') {
                    const val = isThreshold ? value * 2.20462 : value;
                    return parseFloat(val.toFixed(1));
                }
                return parseFloat(value.toFixed(1));
            }
            return parseFloat(value.toFixed(1));
        };
    }, [vitalConfig, unitType]);

    const convertedUpperLimit = convertValue(vitalConfig?.upperLimit, true);
    const convertedLowerLimit = convertValue(vitalConfig?.lowerLimit, true);
    const convertedTargetValue = convertValue(vitalConfig?.targetValue, true);

    // Get highest and lowest measurements from actual data
    const measurementValues = useMemo(() => {
        return chartValues
            .map(m => m.measurement)
            .filter(val => val !== null && val !== undefined && !isNaN(val));
    }, [chartValues]);
    
    const highestMeasurement = measurementValues.length > 0 ? Math.max(...measurementValues) : null;
    const lowestMeasurement = measurementValues.length > 0 ? Math.min(...measurementValues) : null;
    const convertedHighest = convertValue(highestMeasurement, false);
    const convertedLowest = convertValue(lowestMeasurement, false);

    // Calculate domain using same logic as PerinComposedChart
    const domain = useMemo(() => {
        if (!vitalConfig || !modalityId) return { formatted: [0, 100], raw: [0, 100] };
        
        const hasLowerLimit = convertedLowerLimit !== 0 && convertedLowerLimit !== null && convertedLowerLimit !== undefined;
        const hasUpperLimit = convertedUpperLimit !== 0 && convertedUpperLimit !== null && convertedUpperLimit !== undefined;
        const hasMeasurements = convertedLowest !== null && convertedLowest !== undefined && 
                                convertedHighest !== null && convertedHighest !== undefined && convertedHighest !== 0;

        // Start with actual measurement values if available
        let minDomain = hasMeasurements ? convertedLowest : (hasLowerLimit ? convertedLowerLimit : 0);
        let maxDomain = hasMeasurements ? convertedHighest : (hasUpperLimit ? convertedUpperLimit : 100);

        // Expand domain to include thresholds if they're outside the measurement range
        if (hasLowerLimit && hasMeasurements && convertedLowerLimit < convertedLowest) {
            minDomain = convertedLowerLimit;
        }
        if (hasUpperLimit && hasMeasurements && convertedUpperLimit > convertedHighest) {
            maxDomain = convertedUpperLimit;
        }

        const isSingleValue = convertedLowest === convertedHighest && convertedLowest !== null && convertedLowest !== undefined;
        
        let range = maxDomain - minDomain;
        
        // If range is 0 or very small (single value case), create a reasonable range
        if (range === 0 || (isSingleValue && range < 0.01)) {
            const singleValue = convertedLowest !== null ? convertedLowest : (convertedHighest !== null ? convertedHighest : 0);
            const padding = Math.max(Math.abs(singleValue) * 0.1, 1);
            minDomain = singleValue - padding;
            maxDomain = singleValue + padding;
            range = maxDomain - minDomain;
        }

        const bufferPercentage = 0.2;
        const buffer = range * bufferPercentage;
        const newMin = minDomain - buffer < 0 && minDomain >= 0 ? minDomain : minDomain - buffer;
        const newMax = maxDomain + buffer;

        const formattedMin = formatValueByModalityId(newMin, modalityId);
        const formattedMax = formatValueByModalityId(newMax, modalityId);
        return { 
            formatted: [formattedMin, formattedMax],
            raw: [newMin, newMax]
        };
    }, [vitalConfig, modalityId, convertedLowerLimit, convertedUpperLimit, convertedHighest, convertedLowest]);

    // Generate ticks with more intermediate values within the range
    const ticks = useMemo(() => {
        if (!vitalConfig || !domain || !modalityId) return [];
        
        // Use raw numeric values from domain for accurate tick calculation
        const minValue = domain.raw[0];
        const maxValue = domain.raw[1];
        
        // Generate evenly spaced ticks (approximately 6 ticks to match grid rows)
        const numTicks = 6;
        const range = maxValue - minValue;
        const step = range / (numTicks - 1);
        
        let tickArray = [];
        
        // Add evenly spaced intermediate ticks
        for (let i = 0; i < numTicks; i++) {
            const tickValue = minValue + (step * i);
            tickArray.push(tickValue);
        }
        
        // Add threshold values only if they're within the domain range
        if (convertedLowerLimit !== 0 && convertedLowerLimit !== null && convertedLowerLimit !== undefined) {
            if (convertedLowerLimit >= minValue && convertedLowerLimit <= maxValue) {
                tickArray.push(convertedLowerLimit);
            }
        }
        if (convertedTargetValue !== 0 && convertedTargetValue !== null && convertedTargetValue !== undefined) {
            if (convertedTargetValue >= minValue && convertedTargetValue <= maxValue) {
                tickArray.push(convertedTargetValue);
            }
        }
        if (convertedUpperLimit !== 0 && convertedUpperLimit !== null && convertedUpperLimit !== undefined) {
            if (convertedUpperLimit >= minValue && convertedUpperLimit <= maxValue) {
                tickArray.push(convertedUpperLimit);
            }
        }


        const uniqueTicks = Array.from(new Set(tickArray.filter(tick => tick !== undefined && tick !== null && !isNaN(tick))))
            .sort((a, b) => b - a)
            .map((item) => formatValueByModalityId(item, modalityId));
        
        // Remove duplicates after formatting (in case formatting produces same values)
        return Array.from(new Set(uniqueTicks));
    }, [vitalConfig, domain, modalityId, convertedLowerLimit, convertedTargetValue, convertedUpperLimit]);

    const chartData = useMemo(() => {
        if (!vitalConfig || !chartValues || chartValues.length === 0) {
            return { labels: [], data: [], measurementData: [] };
        }


        const sortedValues = [...chartValues].sort((a, b) => a.sortTime - b.sortTime);
        const groupedByDate = {};
        sortedValues.forEach(m => {
            const dateKey = m.date || m.groupKey || `${m.formattedDate}-${m.timeStamp}`;
            if (!groupedByDate[dateKey]) {
                groupedByDate[dateKey] = {
                    label: m.date,
                    timeStamp: m.timeStamp,
                    sortTime: m.sortTime,
                    measurements: [],
                    measurementObjects: [] // Store full measurement objects for onClick
                };
            }
            groupedByDate[dateKey].measurements.push(m.measurement);
            groupedByDate[dateKey].measurementObjects.push(m); // Store full measurement object
        });

        // Extract labels and data (use average if multiple devices at same time)
        const sortedKeys = Object.keys(groupedByDate).sort((a, b) => 
            groupedByDate[a].sortTime - groupedByDate[b].sortTime
        );
        
        const labels = sortedKeys.map(key => groupedByDate[key].label);
        const data = sortedKeys.map(key => {
            const measurements = groupedByDate[key].measurements.filter(v => v !== null && v !== undefined && !isNaN(v));
            if (measurements.length === 0) return null;
            // Average if multiple devices, otherwise use single value
            return measurements.length > 1 
                ? measurements.reduce((sum, val) => sum + val, 0) / measurements.length
                : measurements[0];
        });
        
        // Store measurement data for each data point (use first measurement if multiple devices)
        const measurementData = sortedKeys.map(key => {
            const measurementObjects = groupedByDate[key].measurementObjects;
            return measurementObjects.length > 0 ? measurementObjects[0] : null;
        });

        return { labels, data, measurementData };
    }, [chartValues, vitalConfig]);

    const labelCount = chartData.labels.length;
    


    const datasets = useMemo(() => {
        if (!vitalConfig || chartData.data.length === 0) return [];
        
        const mainDataset = {
            label: vitalConfig.title || 'Measurement',
            data: chartData.data,
            borderColor: '#496ca3',
            backgroundColor: 'rgba(73, 108, 163, 0.1)',
            pointRadius: 4,
            pointBackgroundColor: '#ffffff',
            pointBorderColor: '#496ca3',
            pointBorderWidth: 2,
            tension: 0,
            fill: false,
            order: 3
        };

        const result = [mainDataset];

        if (convertedUpperLimit !== 0 && convertedUpperLimit !== null && convertedUpperLimit !== undefined && labelCount > 0) {
            result.push({
                label: 'Upper Limit',
                data: Array(labelCount).fill(convertedUpperLimit),
                borderColor: '#CA3B3B',
                backgroundColor: 'transparent',
                borderDash: [3, 3],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                order: 1,
                yAxisID: 'y'
            });
        }

        if (convertedLowerLimit !== 0 && convertedLowerLimit !== null && convertedLowerLimit !== undefined && labelCount > 0) {
            result.push({
                label: 'Lower Limit',
                data: Array(labelCount).fill(convertedLowerLimit),
                borderColor: '#CA3B3B',
                backgroundColor: 'transparent',
                borderDash: [3, 3],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                order: 1,
                yAxisID: 'y'
            });
        }

        if (convertedTargetValue !== 0 && convertedTargetValue !== null && convertedTargetValue !== undefined && labelCount > 0) {
            result.push({
                label: 'Target Value',
                data: Array(labelCount).fill(convertedTargetValue),
                borderColor: 'green',
                backgroundColor: 'transparent',
                borderDash: [3, 3],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                order: 2,
                yAxisID: 'y'
            });
        }

        return result;
    }, [vitalConfig, convertedUpperLimit, convertedLowerLimit, convertedTargetValue, labelCount, chartData]);

    // X-axis labels from chart data labels - limit to max 6-8 labels evenly spaced
    const xAxisLabels = useMemo(() => {
        if (!chartData.labels || chartData.labels.length === 0) return [];
        if (!chartValues || chartValues.length === 0) return [];
        
        // Sort measurements by time
        const sortedValues = [...chartValues].sort((a, b) => a.sortTime - b.sortTime);
        
        // Limit to max 6 labels to prevent overflow
        const maxLabels = 6;
        const totalLabels = sortedValues.length;
        
        if (totalLabels <= maxLabels) {
            // Show all labels if we have 6 or fewer
            return sortedValues.map((measurement, idx) => {
                if (!measurement.timeStamp) {
                    return {
                        time: measurement.date || measurement.formattedDate || '',
                        date: null,
                        fullDate: measurement.date || measurement.formattedDate || '',
                        index: idx
                    };
                }
                
                const dateObj = new Date(measurement.timeStamp);
                const hour = dateObj.getHours();
                const minute = dateObj.getMinutes();
                const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const timeStr = `${hour12}:${String(minute).padStart(2, '0')}${ampm}`;
                
                return {
                    time: timeStr,
                    date: measurement.formattedDate || measurement.date || null,
                    fullDate: measurement.formattedDate || measurement.date || '',
                    index: idx
                };
            });
        }
        
        // Show evenly spaced labels (first, last, and evenly distributed in between)
        const step = Math.floor((totalLabels - 1) / (maxLabels - 1));
        const indices = [];
        
        // Always include first and last
        indices.push(0);
        for (let i = step; i < totalLabels - 1; i += step) {
            if (indices.length < maxLabels - 1) {
                indices.push(i);
            }
        }
        if (indices[indices.length - 1] !== totalLabels - 1) {
            indices.push(totalLabels - 1);
        }
        
        return indices.map((index) => {
            const measurement = sortedValues[index];
            if (!measurement || !measurement.timeStamp) {
                return {
                    time: measurement?.date || measurement?.formattedDate || '',
                    date: null,
                    fullDate: measurement?.date || measurement?.formattedDate || '',
                    index: index
                };
            }
            
            const dateObj = new Date(measurement.timeStamp);
            const hour = dateObj.getHours();
            const minute = dateObj.getMinutes();
            const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const timeStr = `${hour12}:${String(minute).padStart(2, '0')}${ampm}`;
            
            return {
                time: timeStr,
                date: measurement.formattedDate || measurement.date || null,
                fullDate: measurement.formattedDate || measurement.date || '',
                index: index
            };
        });
    }, [chartData.labels, chartValues]);

    // Early return after all hooks
    if (!vitalConfig || chartData.data.length === 0) {
        return null;
    }

    const chartDataWithDatasets = {
        labels: chartData.labels,
        datasets: datasets
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
            // Handle point click
            if (onPointClick && elements && elements.length > 0) {
                const element = elements[0];
                const dataIndex = element.index;
                
                // Only handle clicks on the main dataset (index 0)
                if (element.datasetIndex === 0 && chartData.measurementData && chartData.measurementData[dataIndex]) {
                    const measurement = chartData.measurementData[dataIndex];
                    onPointClick(measurement, vitalConfig);
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                filter: (tooltipItem) => {
                    // Only show tooltip for main data line, not reference lines/areas
                    return tooltipItem.datasetIndex === 0;
                },
                callbacks: {
                    title: (tooltipItems) => {
                        // Show date/time in tooltip title
                        const tooltipItem = tooltipItems[0];
                        if (!tooltipItem) return '';
                        
                        const dataIndex = tooltipItem.dataIndex;
                        if (xAxisLabels && xAxisLabels[dataIndex]) {
                            const label = xAxisLabels[dataIndex];
                            if (label.date) {
                                return `${label.date} ${label.time}`;
                            }
                            return label.time || label.fullDate || '';
                        }
                        return chartData.labels[dataIndex] || '';
                    },
                    label: (tooltipItem) => {
                        // Format the value with unit
                        if (tooltipItem.datasetIndex !== 0) return '';
                        
                        const rawValue = tooltipItem.parsed.y;
                        if (rawValue === null || rawValue === undefined) return '';
                        
                        // Format the value using the modality formatter
                        const formattedValue = formatValueByModalityId(rawValue, modalityId);
                        const unit = vitalConfig?.unit || '';
                        
                        const label = tooltipItem.dataset.label || vitalConfig?.title || 'Measurement';
                        return `${label}: ${formattedValue}${unit ? ` ${unit}` : ''}`;
                    }
                },
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#496ca3',
                borderWidth: 1,
                padding: 10,
                displayColors: true,
                titleFont: {
                    size: 12,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 12
                }
            }
        },
        scales: {
            y: {
                min: domain.formatted[0],
                max: domain.formatted[1],
                ticks: {
                    stepSize: undefined,
                    display: false
                },
                grid: {
                    display: false
                },
                border: {
                    display: false
                }
            },
            x: {
                ticks: {
                    display: false
                },
                grid: {
                    display: false
                },
                border: {
                    display: false
                }
            }
        },
        elements: {
            point: {
                hoverRadius: 6
            }
        }
    };

    // Y-axis labels from calculated ticks
    const yAxisLabels = ticks;
    // Create grid rows (12 rows total)
    const gridRows = Array.from({ length: 12 }, (_, i) => i);

    return (
        <div style={{display: 'flex', gap: '0', flexDirection: 'column', width: '100%'}}>
            {/* Chart Labels Section */}
            <div className={styles.ChartLabelsSection}>
                <div className={styles.MainLabel}>
                    <p className={styles.VitalNameText}>
                        {vitalConfig.title}
                    </p>
                </div>
            </div>

            <div className={styles.ChartLabelsSection}>
                {vitalConfig.unit && (
                    <p className={styles.VitalUnitText}>
                        {vitalConfig.unit}
                    </p>
                )}
                <div className={styles.ChartFilterContent}>
                    <div className={styles.LegendColor}></div>
                    <div className={styles.LegendText}>
                        <p>{vitalConfig.legend}</p>
                    </div>
                </div>
            </div>

            <div className={styles.ChartContainer}>
                <div className={styles.YAxisLabels}>
                    {yAxisLabels.map((label, index) => (
                        <div key={`y-axis-label-${index}-${label}`} className={styles.YAxisLabel}>
                            <p className={styles.YAxisLabelText}>{label}</p>
                        </div>
                    ))}
                </div>

                <div className={styles.ChartGrid} style={{ position: 'relative' }}>
                    <div className={styles.GraphBoxes}>
                        {gridRows.map((rowIndex) => {
                            const isTopRow = rowIndex === 0;
                            const isBottomRow = rowIndex === gridRows.length - 1;
                            
                            return (
                                <React.Fragment key={rowIndex}>
                                    <div 
                                        className={`${styles.GraphRow} ${
                                            isTopRow ? styles.GraphRowTop : 
                                            isBottomRow ? styles.GraphRowBottom : 
                                            ''
                                        }`}
                                    >
                                        {Array.from({ length: 6 }, (_, colIndex) => {
                                            const isLastCol = colIndex === 5;
    
                                            
                                            return (
                                                <div
                                                    key={colIndex}
                                                    className={`${styles.GraphSquare} ${
                                                        styles.GraphSquareWhite
                                                    } ${isLastCol ? styles.GraphSquareLast : ''}`}
                                                />
                                            );
                                        })}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <div style={{ 
                        position: 'absolute', 
                        left: '8px', 
                        top: '0', 
                        width: 'calc(100% - 16px)', 
                        height: '500px',
                        pointerEvents: onPointClick ? 'auto' : 'none',
                        cursor: onPointClick ? 'pointer' : 'default'
                    }}>
                        <Line data={chartDataWithDatasets} options={chartOptions} />
                    </div>

                    <div className={styles.XAxisLabels}>
                        {xAxisLabels.length > 0 ? (
                            xAxisLabels.map((label, index) => (
                                <div key={`x-axis-label-${label.index}-${index}`} className={styles.XAxisLabel}>
                                    <p className={`${styles.XAxisLabelText} ${styles.XAxisLabelTextTop}`}>
                                        {label.time}
                                    </p>
                                    {label.date && (
                                        <p className={`${styles.XAxisLabelText} ${styles.XAxisLabelTextBottom}`}>
                                            {label.date}
                                        </p>
                                    )}
                                </div>
                            ))
                        ) : chartData.labels.length > 0 ? (
                            chartData.labels.map((label, index) => (
                                <div key={index} className={styles.XAxisLabel}>
                                    <p className={styles.XAxisLabelText}>{label}</p>
                                </div>
                            ))
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InpatientVitalChart;

